import React, { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import './styles.scss';
import { BsCalendar3 } from '@react-icons/all-files/bs/BsCalendar3';
import { DropdownComponent } from 'Components/molecules';
import ErrorIcon from './ErrorIcon';

import { CondEntry, DATETIME_CUSTOM_SENTINEL, DATETIME_SPECIAL_VALUES } from 'Core-utils/types/rules.types';
import { isArrayOperator } from 'Core-utils/helpers/ruleBuilderHelpers';
import { RuleChipInput } from 'Components/molecules/ChipInput';
import { FIELD_TYPES, OPERATORS_WITHOUT_VALUE } from 'Core-utils/constants/constants';

export interface ValueInputProps {
    cond: CondEntry;
    onChange: (patch: Partial<CondEntry>) => void;
}

// Items shown in the datetime dropdown — presets + Custom date entry point
const DATETIME_DROPDOWN_ITEMS = [
    ...DATETIME_SPECIAL_VALUES.map((d) => ({ id: d.value, displayValue: d.label, value: d.value })),
    { id: DATETIME_CUSTOM_SENTINEL, displayValue: 'Custom date…', value: DATETIME_CUSTOM_SENTINEL },
];

// Convert DD/MM/YYYY → YYYY-MM-DD for the native date input.
// Returns '' for any partial input (e.g. "05/06" with no year) so the hidden
// date input never receives a malformed value like "--05-06".
const toIso = (ddmmyyyy: string): string => {
    const parts = ddmmyyyy.split('/');
    if (parts.length !== 3) {
        return '';
    }
    const [dd, mm, yyyy] = parts;
    if (!dd || !mm || !yyyy || yyyy.length < 4) {
        return '';
    }
    return `${yyyy}-${mm}-${dd}`;
};

// Convert YYYY-MM-DD → DD/MM/YYYY for display
const fromIso = (iso: string): string => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

// ── Segmented masked date input ───────────────────────────────────────────────
// Renders three separate tiny inputs (DD / MM / YYYY) so the mask is always
// visible and focus auto-advances between segments as the user types.
interface MaskedDateInputProps {
    value: string;                  // '' | DATETIME_CUSTOM_SENTINEL | 'DD/MM/YYYY'
    onChange: (v: string) => void;  // emits '' or 'DD/MM/YYYY'
    isInvalid?: boolean;
    errorMessage?: string | null;
    onCalendarClick: () => void;
    hiddenInputRef: React.RefObject<HTMLInputElement>;
}

const MaskedDateInput = ({ value, onChange, isInvalid, errorMessage, onCalendarClick, hiddenInputRef }: MaskedDateInputProps) => {
    const ddRef = useRef<HTMLInputElement>(null);
    const mmRef = useRef<HTMLInputElement>(null);
    const yyyyRef = useRef<HTMLInputElement>(null);

    // Parse 'DD/MM/YYYY' into three segments; treat sentinel as empty
    const raw = value === DATETIME_CUSTOM_SENTINEL ? '' : value;
    const [ddVal = '', mmVal = '', yyyyVal = ''] = raw.split('/');

    const emit = (dd: string, mm: string, yyyy: string) => {
        if (!dd && !mm && !yyyy) {
            onChange('');
            return;
        }
        onChange(`${dd}/${mm}/${yyyy}`);
    };

    const handleDD = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 2);
        emit(v, mmVal, yyyyVal);
        if (v.length === 2) {
            mmRef.current?.focus();
            mmRef.current?.select();
        }
    };

    const handleMM = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 2);
        emit(ddVal, v, yyyyVal);
        if (v.length === 2) {
            yyyyRef.current?.focus();
            yyyyRef.current?.select();
        }
    };

    const handleYYYY = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 4);
        emit(ddVal, mmVal, v);
    };

    const handleKeyDownMM = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !mmVal) {
            e.preventDefault();
            ddRef.current?.focus();
            ddRef.current?.select();
        }
    };

    const handleKeyDownYYYY = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !yyyyVal) {
            e.preventDefault();
            mmRef.current?.focus();
            mmRef.current?.select();
        }
    };

    const segStyle: React.CSSProperties = {
        fontFamily: 'monospace',
        fontSize: '11px',
        textAlign: 'center',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 0,
    };

    return (
        <div className={`rb-masked-date${isInvalid ? ' rb-masked-date--invalid' : ''}`}>
            <input
                ref={ddRef}
                style={{ ...segStyle, width: 22 }}
                placeholder="DD"
                maxLength={2}
                value={ddVal}
                onChange={handleDD}
                onFocus={(e) => e.target.select()}
            />
            <span className="rb-masked-date__sep">/</span>
            <input
                ref={mmRef}
                style={{ ...segStyle, width: 22 }}
                placeholder="MM"
                maxLength={2}
                value={mmVal}
                onChange={handleMM}
                onKeyDown={handleKeyDownMM}
                onFocus={(e) => e.target.select()}
            />
            <span className="rb-masked-date__sep">/</span>
            <input
                ref={yyyyRef}
                style={{ ...segStyle, width: 36 }}
                placeholder="YYYY"
                maxLength={4}
                value={yyyyVal}
                onChange={handleYYYY}
                onKeyDown={handleKeyDownYYYY}
                onFocus={(e) => e.target.select()}
            />
            <BsCalendar3
                size={13}
                className="rb-value-date__icon"
                onClick={onCalendarClick}
                title="Open date picker"
            />
            {isInvalid && errorMessage && <ErrorIcon message={errorMessage} />}
            {/* Hidden native date input for the calendar picker */}
            <input
                ref={hiddenInputRef}
                type="date"
                className="rb-date-hidden-input"
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                value={toIso(raw)}
                onChange={(e) => {
                    if (!e.target.value) {
                        return;
                    }
                    onChange(fromIso(e.target.value));
                }}
            />
        </div>
    );
};

const ValueInput = ({ cond, onChange }: ValueInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-open the native date picker the moment user selects "Custom date…"
    useEffect(() => {
        if (cond.value === DATETIME_CUSTOM_SENTINEL) {
            // Small delay so the input mounts before we try to open the picker
            const t = setTimeout(() => {
                const el = inputRef.current;
                if (!el) {
                    return;
                }
                if ('showPicker' in el) {
                    try {
                        (el as any).showPicker();
                    } catch {
                        /* picker unavailable */
                    }
                }
            }, 50);
            return () => clearTimeout(t);
        }
    }, [cond.value]);

    const openPicker = () => {
        const el = inputRef.current;
        if (!el) {
            return;
        }
        if ('showPicker' in el) {
            try {
                (el as any).showPicker();
            } catch {
                (el as HTMLInputElement).focus();
            }
        } else {
            (el as HTMLInputElement).focus();
        }
    };

    const getValidationError = (): string | null => {
        const val = cond.value;
        const isSentinel = val === DATETIME_CUSTOM_SENTINEL;
        const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '');

        if (isEmpty || isSentinel) {
            return 'Value is required';
        }
        // datetime: only presets or a full DD/MM/YYYY date are valid

        const fType = String(cond.fieldType || '').toLowerCase();
        if (fType === FIELD_TYPES.NUMERIC) {
            if (isNaN(Number(val))) {
                return 'Must be a valid number';
            }
        }
        if (fType === FIELD_TYPES.BOOLEAN) {
            const isBool = typeof val === 'boolean' || val === 'true' || val === 'false';
            if (!isBool) {
                return 'Must be true or false';
            }
        }
        return null;
    };

    const error = getValidationError();

    if (OPERATORS_WITHOUT_VALUE.includes(cond.operator)) {
        return null;
    }

    if (isArrayOperator(cond.operator)) {
        return (
            <RuleChipInput
                value={Array.isArray(cond.value) ? cond.value : []}
                onChange={(v) => onChange({ value: v })}
            />
        );
    }

    if (cond.fieldType === FIELD_TYPES.DATETIME) {
        const currentValue = typeof cond.value === 'string' ? cond.value : '';
        const isPreset = DATETIME_SPECIAL_VALUES.some((d) => d.value === currentValue);
        const isEmpty = currentValue === '';
        // Custom mode: user explicitly chose "Custom date…" (sentinel) OR has already typed/picked a date
        const isCustomMode = !isPreset && !isEmpty;
        // The actual DD/MM/YYYY string — empty when sentinel (just switched to custom, no date yet)
        const customDateValue = isCustomMode && currentValue !== DATETIME_CUSTOM_SENTINEL
            ? currentValue
            : '';

        // Dropdown label:
        //   ''                  → 'Select…'  (nothing chosen yet)
        //   NOW / TODAY         → preset label
        //   sentinel / date str → 'Custom date…'
        const dropdownLabel = isEmpty
            ? 'Select…'
            : isPreset
            ? (DATETIME_SPECIAL_VALUES.find((d) => d.value === currentValue)?.label ?? 'Select…')
            : 'Custom date…';

        const handleDropdownChange = (v: string | null) => {
            if (v === DATETIME_CUSTOM_SENTINEL) {
                onChange({ value: DATETIME_CUSTOM_SENTINEL });
            } else {
                onChange({ value: v ?? '' });
            }
        };

        return (
            <div className="rb-value-date">
                {/* Always-visible dropdown — lets user choose preset or custom */}
                <div className={`rb-value-dropdown${isEmpty ? ' rb-value-dropdown--unset' : ''}`}>
                    <DropdownComponent
                        value={dropdownLabel}
                        dropDownItems={DATETIME_DROPDOWN_ITEMS}
                        onChange={handleDropdownChange}
                        variant="filled"
                    />
                </div>

                {/* Segmented masked input — only shown once user selects "Custom date…" */}
                {isCustomMode && (
                    <MaskedDateInput
                        value={customDateValue}
                        isInvalid={!!error}
                        errorMessage={error}
                        onChange={(v) => onChange({ value: v || DATETIME_CUSTOM_SENTINEL })}
                        onCalendarClick={openPicker}
                        hiddenInputRef={inputRef}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="rb-value-field-wrap rb-input-icon-wrap">
            <Form.Control
                className="rb-input-small"
                type={cond.fieldType === FIELD_TYPES.NUMERIC ? 'number' : 'text'}
                isInvalid={!!error}
                value={(cond.value as string | number | string[] | undefined) ?? ''}
                placeholder="value"
                onChange={(e) => onChange({ value: e.target.value })}
                style={{ fontFamily: 'monospace' }}
            />
            {error && <ErrorIcon message={error} />}
        </div>
    );
};

export default ValueInput;
